import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

interface WatermarkSettingsProps {
  enabled: boolean;
  size: number;
  quality: number;
  squareOutput: boolean;
  brightness: number;
  supabaseQuality: number;
  onEnabledChange: (enabled: boolean) => void;
  onSizeChange: (size: number) => void;
  onQualityChange: (quality: number) => void;
  onSquareOutputChange: (enabled: boolean) => void;
  onBrightnessChange: (brightness: number) => void;
  onSupabaseQualityChange: (quality: number) => void;
  onClose: () => void;
}

export function WatermarkSettings({
  enabled,
  size,
  quality,
  squareOutput,
  brightness,
  supabaseQuality,
  onEnabledChange,
  onSizeChange,
  onQualityChange,
  onSquareOutputChange,
  onBrightnessChange,
  onSupabaseQualityChange,
  onClose
}: WatermarkSettingsProps) {
  // ローカル状態で設定を管理
  const [localEnabled, setLocalEnabled] = useState(enabled);
  const [localSize, setLocalSize] = useState(size);
  const [localQuality, setLocalQuality] = useState(quality);
  const [localSquareOutput, setLocalSquareOutput] = useState(squareOutput);
  const [localBrightness, setLocalBrightness] = useState(brightness);
  const [localSupabaseQuality, setLocalSupabaseQuality] = useState(supabaseQuality);
  const [hasChanges, setHasChanges] = useState(false);

  // 設定変更を検知
  useEffect(() => {
    const changed = 
      localEnabled !== enabled ||
      localSize !== size ||
      localQuality !== quality ||
      localSquareOutput !== squareOutput ||
      localBrightness !== brightness ||
      localSupabaseQuality !== supabaseQuality;
    setHasChanges(changed);
  }, [localEnabled, localSize, localQuality, localSquareOutput, localBrightness, localSupabaseQuality, enabled, size, quality, squareOutput, brightness, supabaseQuality]);

  // 保存処理
  const handleSave = () => {
    console.log('🔧 画像設定保存:', {
      'Supabase画質': `${localSupabaseQuality}%`,
      '画質': `${localQuality}%`,
      'ウォーターマーク': localEnabled,
      'スクエア出力': localSquareOutput,
      '明るさ': `${localBrightness}%`
    });
    
    onEnabledChange(localEnabled);
    onSizeChange(localSize);
    onQualityChange(localQuality);
    onSquareOutputChange(localSquareOutput);
    onBrightnessChange(localBrightness);
    onSupabaseQualityChange(localSupabaseQuality);
    onClose();
    setHasChanges(false);
  };

  // リセット処理
  const handleReset = () => {
    setLocalEnabled(enabled);
    setLocalSize(size);
    setLocalQuality(quality);
    setLocalSquareOutput(squareOutput);
    setLocalBrightness(brightness);
    setLocalSupabaseQuality(supabaseQuality);
    setHasChanges(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-md w-full mx-4 ">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-medium text-gray-900">画像設定</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
          {/* Square Output Setting */}
          <div className="space-y-2">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={localSquareOutput}
                onChange={(e) => setLocalSquareOutput(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">
                写真をスクエアで出力する
              </span>
            </label>
            <p className="text-sm text-gray-500 ml-6">
              すべての画像を正方形に切り抜きます
            </p>
          </div>

          {/* Watermark Settings */}
          <div className="space-y-2 pt-4 border-t">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={localEnabled}
                onChange={(e) => setLocalEnabled(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">
                管理番号を画像に埋め込む
              </span>
            </label>
            <p className="text-sm text-gray-500 ml-6">
              画像の左下に管理番号を白い縁取り付きで追加します
            </p>
          </div>

          {localEnabled && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                文字サイズ ({localSize}%)
              </label>
              <input
                type="range"
                min="10"
                max="200"
                value={localSize}
                onChange={(e) => setLocalSize(Number(e.target.value))}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>小</span>
                <span>大</span>
              </div>
            </div>
          )}

          {/* Compression Quality Settings */}
          <div className="space-y-2 pt-4 border-t">
            <label className="block text-sm font-medium text-gray-700">
              ZIP書き出し画質 20% 推奨　({localQuality}%)
            </label>
            <input
              type="range"
              min="10"
              max="100"
              value={localQuality}
              onChange={(e) => setLocalQuality(Number(e.target.value))}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-gray-500">
              <span>低画質・小サイズ</span>
              <span>高画質・大サイズ</span>
            </div>
            <p className="text-sm text-gray-500 mt-2">
              画質を下げるとファイルサイズを小さくできます。
              {localQuality < 50 && '低画質では画像が粗くなる可能性があります。'}
            </p>
          </div>
          
          {/* Brightness Settings - Moved to bottom */}
          <div className="space-y-2 pt-4 border-t">
            <label className="block text-sm font-medium text-gray-700">
              明るさ ({localBrightness}%)
            </label>
            <input
              type="range"
              min="50"
              max="200"
              value={localBrightness}
              onChange={(e) => setLocalBrightness(Number(e.target.value))}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-gray-500">
              <span>暗く</span>
              <span className="ml-4 mr-4">標準</span>
              <span>明るく</span>
            </div>
          </div>

          {/* Supabase Upload Quality Settings */}
          <div className="space-y-2 pt-4 border-t">
            <label className="block text-sm font-medium text-gray-700">
              アプリへの取込画質 20% 推奨　({localSupabaseQuality}%)
            </label>
            <input
              type="range"
              min="10"
              max="100"
              value={localSupabaseQuality}
              onChange={(e) => setLocalSupabaseQuality(Number(e.target.value))}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-gray-500">
              <span>超高圧縮・極小サイズ</span>
              <span>高画質・大サイズ</span>
            </div>
            <p className="text-sm text-gray-500 mt-2">
              Supabaseにアップロードする画像の画質を設定します。
              {localSupabaseQuality < 50 && '低画質設定ではアップロード速度が大幅に向上し、ストレージ容量を大幅に節約できます。'}
              {localSupabaseQuality >= 50 && localSupabaseQuality < 80 && '画質を下げるとアップロード速度が向上し、ストレージ容量を節約できます。'}
            </p>
          </div>
        </div>

        <div className="flex justify-between px-6 py-4 bg-gray-50 rounded-b-lg">
          <div className="flex gap-2">
            {hasChanges && (
              <button
                onClick={handleReset}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                リセット
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              キャンセル
            </button>
            <button
              onClick={handleSave}
              disabled={!hasChanges}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                hasChanges 
                  ? 'text-white bg-blue-600 hover:bg-blue-700' 
                  : 'text-gray-400 bg-gray-300 cursor-not-allowed'
              }`}
            >
              保存
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}